import { useMemo } from "react";
import PropTypes from "prop-types";
import "./InputAvatarComponent.scss";

const InputAvatarComponent = ({
  id,
  title,
  description,
  options = [],
  formik,
  defaultValue,
  hide,
  required = false,
  isEditable = true,
  fromWorkflow = false,
  handleFormBlur,
}) => {
  const selectedValue = formik?.values?.[id];

  const selectedId = useMemo(() => {
    if (!selectedValue) return "";
    if (typeof selectedValue === "string") {
      return selectedValue;
    }
    if (typeof selectedValue === "object") {
      return selectedValue?.id || selectedValue?.optionId || "";
    }
    return "";
  }, [selectedValue]);

  const buildOptionPayload = (option) => {
    const fields = Array.isArray(option?.fields) ? option.fields[0] || {} : {};
    return {
      id: option?.id,
      optionId: option?.id,
      videoId: fields?.videoId || "",
      audioId: fields?.audioId || "",
      profilePic: fields?.profilePic || "",
      backgroundPic: fields?.backgroundPic || "",
      aboutAvatar: fields?.aboutAvatar || "",
    };
  };

  const handleSelect = (option) => {
    if (!isEditable) {
      return;
    }

    const payload = buildOptionPayload(option);
    formik.setFieldValue(id, payload);
    formik.setFieldTouched(id, true, false);

    if (fromWorkflow && typeof handleFormBlur === "function") {
      handleFormBlur({
        target: {
          name: id,
          value: payload,
        },
      });
    }
  };

  const renderMetaRow = (label, value) => {
    if (!value) return null;
    return (
      <div className="avatar-metadata">
        <span className="metadata-label">{label}:</span>
        <span>{value}</span>
      </div>
    );
  };

  return (
    <div className="avatar-input-wrapper">
      <label htmlFor={`${id}-hidden-input`}>
        <div className="heading-box">
          <h4 className="instruct-title mb-0">{title}</h4>
          {required && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </div>
      </label>
      {description && <div className="mb-2 description">{description}</div>}

      {/* Hidden input enables focus for validation */}
      <input
        id={`${id}-hidden-input`}
        name={id}
        value={selectedId}
        readOnly
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          height: 0,
          width: 0,
        }}
      />

      {Array.isArray(options) && options.length > 0 ? (
        <div className="avatar-card-grid">
          {options.map((option) => {
            const payload = buildOptionPayload(option);
            const isSelected = selectedId ? selectedId === payload.id : false;

            const backgroundStyle = payload.backgroundPic
              ? {
                  backgroundImage: `url(${payload.backgroundPic})`,
                }
              : {};

            const cardClasses = ["avatar-card"];
            if (isSelected) cardClasses.push("selected");
            if (!isEditable) cardClasses.push("disabled");

            return (
              <button
                key={option?.id}
                type="button"
                className={cardClasses.join(" ")}
                onClick={() => handleSelect(option)}
                name={id}
              >
                <div className="avatar-card-header">
                  <span>Avatar </span>
                  {isSelected && <span className="text-primary">Selected</span>}
                </div>

                <div className="avatar-pills">
                  {payload.videoId && <span>Video: {payload.videoId}</span>}
                  {payload.audioId && <span>Audio: {payload.audioId}</span>}
                </div>

                {payload.profilePic && (
                  <div className="avatar-metadata">
                    <span className="metadata-label">Profile</span>
                    <span>{payload.profilePic}</span>
                  </div>
                )}

                {renderMetaRow("Background", payload.backgroundPic)}

                {payload.aboutAvatar && (
                  <div className="avatar-metadata about-text">
                    {payload.aboutAvatar}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-muted" style={{ fontSize: "0.9rem" }}>
          No avatar options available.
        </div>
      )}

      {formik.touched?.[id] && formik.errors?.[id] && (
        <div className="avatar-error-text">{formik.errors[id]}</div>
      )}
    </div>
  );
};

InputAvatarComponent.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.array,
  formik: PropTypes.object.isRequired,
  required: PropTypes.bool,
  isEditable: PropTypes.bool,
  fromWorkflow: PropTypes.bool,
  handleFormBlur: PropTypes.func,
};

export default InputAvatarComponent;
